import {
  ArrowPathIcon,
  ArrowsRightLeftIcon,
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  CreditCardIcon,
  DocumentMinusIcon,
  UserMinusIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

import type { ConfirmableAction } from '../../../hooks/useParcelActionRunner';

/** Assinatura dos ícones do Hero Icons. */
type HeroIcon = typeof XCircleIcon;

/**
 * O que o bloco de detalhe do modal descreve.
 *
 * - `parcel`: a parcela alvo das ações unitárias;
 * - `payment`: a mesma parcela, pelos dados do pagamento a estornar;
 * - `batch`: o conjunto atingido pelas ações em lote;
 * - `contract`: o resumo do contrato que será inativado.
 */
export type ParcelActionScope = 'parcel' | 'payment' | 'batch' | 'contract';

/** Como cada ação se apresenta no modal de confirmação. */
export interface ParcelActionConfig {
  /**
   * Mapeia direto no `type` do `ModalDialog` do ADS: `danger` fixa o ícone de
   * alerta do próprio componente e dá hierarquia destrutiva ao botão primário;
   * `brand` aceita ícone próprio.
   */
  readonly tone: 'danger' | 'brand';
  /** Ícone do dialog. Só tem efeito quando `tone` é `brand`. */
  readonly icon?: HeroIcon;
  /** Ícone do botão de confirmação. */
  readonly confirmIcon: HeroIcon;
  /** O que o bloco de detalhe descreve. */
  readonly scope: ParcelActionScope;
  /**
   * Chaves de i18n literais.
   *
   * Literais, e não montadas a partir do nome da ação, para que o `t()` valide
   * cada uma contra o JSON `pt-BR` em tempo de compilação — uma chave montada em
   * runtime perderia essa checagem.
   */
  readonly titleKey: string;
  readonly descriptionKey: string;
  readonly confirmKey: string;
  /**
   * Rótulo de uma opção booleana oferecida no próprio modal.
   *
   * Presente só nas ações cujo endpoint aceita um parâmetro de decisão que não
   * merece tela — hoje apenas `cancelInvoice`, com o estorno do pagamento. Quem
   * abre o modal é dono do valor, porque é ele que monta a request.
   */
  readonly toggleKey?: string;
  /** Texto de apoio da opção, sob o rótulo. */
  readonly toggleHintKey?: string;
}

/**
 * Configuração das treze ações confirmáveis: tom, ícones, escopo do detalhe e
 * chaves de i18n.
 *
 * Uma entrada por ação, em vez de um mapa por atributo: acrescentar uma ação
 * passa a ser uma linha só, e o `satisfies` acusa o que faltou preencher — antes
 * era preciso lembrar de tocar em cinco estruturas paralelas.
 *
 * Os ícones repetem os do menu da parcela (`parcelOperationItems.ts`) e da régua
 * do cabeçalho, para o operador reconhecer no modal a ação que escolheu.
 */
export const PARCEL_ACTION_CONFIG = {
  cancel: {
    tone: 'danger',
    confirmIcon: XCircleIcon,
    scope: 'parcel',
    titleKey: 'parcels.confirm.cancel.title',
    descriptionKey: 'parcels.confirm.cancel.description',
    confirmKey: 'parcels.confirm.cancel.confirm',
  },
  activate: {
    tone: 'brand',
    icon: CheckCircleIcon,
    confirmIcon: CheckCircleIcon,
    scope: 'parcel',
    titleKey: 'parcels.confirm.activate.title',
    descriptionKey: 'parcels.confirm.activate.description',
    confirmKey: 'parcels.confirm.activate.confirm',
  },
  reversePayment: {
    tone: 'danger',
    confirmIcon: ArrowUturnLeftIcon,
    scope: 'payment',
    titleKey: 'parcels.confirm.reversePayment.title',
    descriptionKey: 'parcels.confirm.reversePayment.description',
    confirmKey: 'parcels.confirm.reversePayment.confirm',
  },
  sendCharge: {
    tone: 'brand',
    icon: CreditCardIcon,
    confirmIcon: CreditCardIcon,
    scope: 'parcel',
    titleKey: 'parcels.confirm.sendCharge.title',
    descriptionKey: 'parcels.confirm.sendCharge.description',
    confirmKey: 'parcels.confirm.sendCharge.confirm',
  },
  takeAdvantageParcel: {
    tone: 'brand',
    icon: ArrowsRightLeftIcon,
    confirmIcon: ArrowsRightLeftIcon,
    scope: 'parcel',
    titleKey: 'parcels.confirm.takeAdvantageParcel.title',
    descriptionKey: 'parcels.confirm.takeAdvantageParcel.description',
    confirmKey: 'parcels.confirm.takeAdvantageParcel.confirm',
  },
  resetIntegration: {
    tone: 'brand',
    icon: ArrowPathIcon,
    confirmIcon: ArrowPathIcon,
    scope: 'parcel',
    titleKey: 'parcels.confirm.resetIntegration.title',
    descriptionKey: 'parcels.confirm.resetIntegration.description',
    confirmKey: 'parcels.confirm.resetIntegration.confirm',
  },
  reintegrate: {
    tone: 'brand',
    icon: ArrowPathIcon,
    confirmIcon: ArrowPathIcon,
    scope: 'parcel',
    titleKey: 'parcels.confirm.reintegrate.title',
    descriptionKey: 'parcels.confirm.reintegrate.description',
    confirmKey: 'parcels.confirm.reintegrate.confirm',
  },
  cancelInvoice: {
    tone: 'danger',
    confirmIcon: DocumentMinusIcon,
    scope: 'parcel',
    titleKey: 'parcels.confirm.cancelInvoice.title',
    descriptionKey: 'parcels.confirm.cancelInvoice.description',
    confirmKey: 'parcels.confirm.cancelInvoice.confirm',
    toggleKey: 'parcels.confirm.cancelInvoice.reversePayment',
    toggleHintKey: 'parcels.confirm.cancelInvoice.reversePaymentHint',
  },
  cancelOpenParcels: {
    tone: 'danger',
    confirmIcon: XCircleIcon,
    scope: 'batch',
    titleKey: 'parcels.confirm.cancelOpenParcels.title',
    descriptionKey: 'parcels.confirm.cancelOpenParcels.description',
    confirmKey: 'parcels.confirm.cancelOpenParcels.confirm',
  },
  openCancelledParcels: {
    tone: 'brand',
    icon: ArrowPathIcon,
    confirmIcon: ArrowPathIcon,
    scope: 'batch',
    titleKey: 'parcels.confirm.openCancelledParcels.title',
    descriptionKey: 'parcels.confirm.openCancelledParcels.description',
    confirmKey: 'parcels.confirm.openCancelledParcels.confirm',
  },
  inactivateFinancial: {
    tone: 'danger',
    confirmIcon: UserMinusIcon,
    scope: 'contract',
    titleKey: 'parcels.confirm.inactivateFinancial.title',
    descriptionKey: 'parcels.confirm.inactivateFinancial.description',
    confirmKey: 'parcels.confirm.inactivateFinancial.confirm',
  },
} as const satisfies Record<ConfirmableAction, ParcelActionConfig>;
